import { TalkingAvatarComponent } from '../bots/talking-avatar/talking-avatar.component';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationSkipped, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService, isEmptyObject, User } from '@core';
import { TLanguage } from '@core/interfaces/TBot';
import { TBotItem } from '@core/interfaces/TBotItem';
import { ImageFallbackDirective } from '@shared/directives/image-fallback.directive';

import { defaultDashboardContent } from '../../constants/defaults';
import { AgentConversationComponent } from '../../features/conversation/components/agent-conversation.component';
import { AgentConversationService } from '../../features/conversation/services/agent-conversation.service';
import { ConversationDataService } from '../../features/conversation/services/conversation-data.service';
import {
  ValidaitorUploadDestinationDialogComponent,
  ValidaitorUploadDestinationDialogResult,
} from '../../features/lmr/components/validaitor-upload-destination-dialog/validaitor-upload-destination-dialog.component';
import { TalkingAvatarService } from '../bots/talking-avatar/talking-avatar.service';
import { BotsService } from '@shared/services/bots.service';
import { FeatureService } from '@shared/services/feature.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { ProjectsService } from '@shared/services/projects.service';
import { StringUtils } from '@shared/utils/stringUtils';

import { AgentSelectorComponent } from './agent-selector/agent-selector.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [
    AgentConversationComponent,
    TalkingAvatarComponent,
    AgentSelectorComponent,
    ImageFallbackDirective,
    MatIconModule,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private _subs: Subscription[] = [];
  public dashboardLogo = StringUtils.brandResourceWithFallback('dashboard');
  public fallbackLogo = StringUtils.brandResourceFallbackPath('sidebarLogo');
  /** Default true so logo shows before metadata resolves; metadata may hide it. */
  protected isLogoVisible = true;

  protected dashboardContent: SafeHtml | undefined;
  protected user: User | null = null;
  protected message: { text: string; files: File[] } | undefined;
  protected conversationBegan = false;
  protected languages: TLanguage[] = [];
  protected formProjects: { name: string; _id: string }[] = [];
  public selectedBot: TBotItem | null = null;

  pendingSuggestion = '';
  displayedGreeting = '';
  infoOpen = false;

  constructor(
    private auth: AuthService,
    private _metadataService: MetadataService,
    private _sanitizer: DomSanitizer,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private _agentConversationService: AgentConversationService,
    private _botsService: BotsService,
    private _projectsService: ProjectsService,
    private _conversationData: ConversationDataService,
    private _talkingAvatarService: TalkingAvatarService,
    private _featureService: FeatureService,
    private _dialog: MatDialog
  ) {}

  get isBotUiConfigEnabled(): boolean {
    return this._featureService.isEnabled('bot-ui-config');
  }

  get isValidatorEnabled(): boolean {
    return this._featureService.isEnabled('validator');
  }

  /** Chat column is on screen (validator docks under input instead of the hero). */
  get isChatVisible(): boolean {
    return !!(this.selectedBot || this.conversationBegan || !this.isBotUiConfigEnabled);
  }

  ngOnInit() {
    this._agentConversationService.newConversation();
    this._talkingAvatarService.reset();
    this._botsService.fetchLanguages();
    this._botsService.fetchBots();
    this._subs.push(
      this._botsService.getLanguagesObservable().subscribe((res: TLanguage[]) => {
        this.languages = res;
      })
    );

    this._subs.push(
      this._projectsService.getProjectsObservable().subscribe((results: any[]) => {
        this.formProjects = results.map(project => ({
          name: project.name,
          _id: project._id as string,
        }));
      })
    );

    this._subs.push(
      this.auth.user().subscribe(value => {
        this.user = value;
        // `user()` replays `{}` before `/me` resolves; do not lock greeting on that first emission.
        if (!isEmptyObject(value)) {
          this.displayedGreeting = `${this.getGreeting()}!`;
        } else {
          this.displayedGreeting = '';
        }
        this.cdr.detectChanges();
      })
    );
    this._projectsService.setProjects();
    this.getDashboardContent();
    this._subs.push(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationSkipped) {
          this._agentConversationService.newConversation();
          this.reset();
        }
      })
    );
    this._subs.push(
      this._conversationData.conversation$.subscribe(conv => {
        if (conv?.bot?._id) {
          this._talkingAvatarService.safeSetSettings(conv.bot)?.then(() => {
            this._talkingAvatarService.handleTalking();
          });
        }
        if (conv?.messages?.length) {
          this.conversationBegan = true;
          this.cdr.markForCheck();
        }
      })
    );

    this.getLogoVisibility();
  }

  private getDashboardContent() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardContent).subscribe({
        next: metadata => {
          if (metadata.value) {
            this.dashboardContent = this._sanitizer.bypassSecurityTrustHtml(metadata.value);
          } else {
            this.dashboardContent = defaultDashboardContent;
          }
        },
        error: () => {
          this.dashboardContent = defaultDashboardContent;
        },
      })
    );
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    const name = this.user?.displayName || this.user?.name || this.user?.email || 'user';
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    return `${greeting}, ${name}`;
  }

  onBotSelected(bot: TBotItem): void {
    this.selectedBot = bot;
    this.cdr.detectChanges();
  }

  protected openValidatorUploadPicker(input: HTMLInputElement): void {
    input.click();
  }

  protected openValidatorUploadWizard(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    if (files.length === 0) {
      return;
    }

    const dialogRef = this._dialog.open<
      ValidaitorUploadDestinationDialogComponent,
      { files: File[] },
      ValidaitorUploadDestinationDialogResult
    >(ValidaitorUploadDestinationDialogComponent, {
      width: '440px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'no-padding-dialog',
      autoFocus: 'first-tabbable',
      data: { files },
    });

    const sub = dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      void this.router.navigate(['/validator/documents'], {
        queryParams: {
          folderId: result.folderId,
          refresh: Date.now(),
        },
      });
    });
    this._subs.push(sub);
  }

  reset() {
    this.conversationBegan = false;
    this.pendingSuggestion = '';
    this.infoOpen = false;
  }

  private getLogoVisibility() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardLogoVisible).subscribe({
        next: metadata => {
          this.isLogoVisible = metadata.value ? metadata.value === 'true' : true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLogoVisible = true;
          this.cdr.detectChanges();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
